/**
 * Risk Rating Configuration Module
 *
 * Isolated configuration mapping that associates Idling_Percentage threshold
 * boundaries with rating labels. Modifying thresholds or rating labels requires
 * changes only to this file.
 *
 * Requirements: 5.1, 5.2, 5.3, 5.4
 */

export const riskRatingConfig = {
  sourceMetric: "idling_percentage",
  thresholds: [
    { min: 0, max: 20, label: "Low", exclusive_max: true },
    { min: 20, max: 50, label: "Medium", exclusive_max: true },
    { min: 50, max: 100, label: "High", exclusive_max: false },
  ],
};

/**
 * Returns the risk rating label for a given idling percentage value.
 *
 * - Values in [0, 20) → "Low"
 * - Values in [20, 50) → "Medium"
 * - Values in [50, 100] → "High"
 * - Values outside 0-100 are clamped to the nearest boundary label:
 *   - Values below 0 → label for lowest threshold ("Low")
 *   - Values above 100 → label for highest threshold ("High")
 *
 * @param {number} idlingPercentage - The idling percentage value
 * @returns {string} The risk rating label ("Low", "Medium", or "High")
 */
export const getRiskRatingLabel = (idlingPercentage) => {
  const { thresholds } = riskRatingConfig;

  // Handle values below the lowest configured boundary
  if (idlingPercentage < thresholds[0].min) {
    return thresholds[0].label;
  }

  // Handle values above the highest configured boundary
  const lastThreshold = thresholds[thresholds.length - 1];
  if (idlingPercentage > lastThreshold.max) {
    return lastThreshold.label;
  }

  // Find the matching threshold
  for (const threshold of thresholds) {
    const aboveMin = idlingPercentage >= threshold.min;
    const belowMax = threshold.exclusive_max
      ? idlingPercentage < threshold.max
      : idlingPercentage <= threshold.max;

    if (aboveMin && belowMax) {
      return threshold.label;
    }
  }

  // Fallback: assign nearest boundary label (should not reach here with proper config)
  return lastThreshold.label;
};
